import { useState } from 'react';
import { Check, Clock, Warning, X } from 'shared/icons';
import { FormatDurationSec } from '../../../utils/format';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { Id } from '../../ui/Mono';
import {
    BEHAVIOR_REPLENISHMENT,
    OpenPauseOf,
    SERVICE_ORDER_STATUS,
    ServiceOrderProgress,
    ServiceOrderStatusVariant,
    ServiceOrderTiming,
} from './serviceOrderModel';

/**
 * Cartão de OS no quadro (§8.1.5).
 *
 * ── O tempo, dito com honestidade ───────────────────────────────────────────
 * O backend carimba `finished_at` também no CANCELAMENTO — sem isso o SLA de
 * uma OS cancelada em execução correria para sempre no painel. Mas esse
 * intervalo NÃO é "tempo de execução" de trabalho concluído: no cartão de uma
 * OS cancelada o número aparece SOB o distintivo "Cancelada" (e nunca sob o
 * rótulo de SLA), que é o que o leitor usa para interpretá-lo. Trabalho
 * interrompido e trabalho concluído não podem somar na mesma coluna mental.
 *
 * ── Sem arrastar e soltar ───────────────────────────────────────────────────
 * O console só pode fazer duas transições (BACKLOG→READY e →CANCELED); DONE é
 * consequência da execução no chão, e IN_PROGRESS é do aceite first-wins do
 * coletor. Um quadro que deixa arrastar para qualquer coluna promete o que o
 * contrato recusa — cada gesto viraria um 409. Por isso as ações são botões
 * explícitos, e só existem onde a transição existe.
 *
 * ── "Negar" CONFIRMA antes (pedido do dono, 11/08) ──────────────────────────
 * Negar não devolve a OS para lugar nenhum: ela vai para CANCELED, que é FIM DE
 * VIDA (não há DELETE nem "descancelar" no módulo). Um clique único num cartão
 * de quadro — do tamanho de um dedo, ao lado de "Aprovar" — destruía trabalho
 * sem pergunta, enquanto a MESMA transição pedida pela ficha já confirmava.
 *
 * A confirmação é a da ficha, no lugar, sem modal sobre o quadro: o botão vira
 * o par "Cancelar OS" (destrutivo, com a CONSEQUÊNCIA no texto — "Negar" não
 * diz que cancela) + "Voltar". Um segundo jeito de confirmar cancelamento no
 * mesmo módulo faria o Supervisor reaprender a mesma decisão em duas telas.
 */
const ServiceOrderCard = ({ order, label, onOpen, onApprove, onDeny, busy = false, canManage = false }) => {
    // Confirmação do NEGAR. Estado do cartão (como o da ficha é da ficha): o
    // quadro tem uma fila inteira, e uma confirmação içada para a página faria
    // duas OSs disputarem o mesmo interruptor.
    const [confirmingDeny, setConfirmingDeny] = useState(false);
    const timing = ServiceOrderTiming(order);
    const progress = ServiceOrderProgress(order);
    const openPause = OpenPauseOf(order);
    const isBacklog = order.status === SERVICE_ORDER_STATUS.BACKLOG;
    const isCanceled = order.status === SERVICE_ORDER_STATUS.CANCELED;
    const isPaused = order.status === SERVICE_ORDER_STATUS.PAUSED;

    return (
        <article className="rounded-lg border border-mono-200 bg-white p-3 shadow-sm-mono transition-colors hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:hover:border-mono-600">
            <button
                type="button"
                onClick={() => onOpen?.(order)}
                className="w-full space-y-2 text-left focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white"
            >
                <div className="flex items-start justify-between gap-2">
                    <Id value={order.code} className="text-sm font-semibold text-black dark:text-white" />
                    <div className="flex shrink-0 flex-wrap justify-end gap-1">
                        {isPaused ? (
                            <Badge variant="warning">{label('wms.service_order.status.PAUSED')}</Badge>
                        ) : null}
                        {isCanceled ? (
                            <Badge variant={ServiceOrderStatusVariant(order.status)}>
                                {label('wms.service_order.status.CANCELED')}
                            </Badge>
                        ) : null}
                        {order.group_id ? <Badge variant="info">{label('wms.service_order.field.group')}</Badge> : null}
                    </div>
                </div>

                <p className="truncate text-sm text-mono-700 dark:text-mono-300">
                    {order.type_label || order.type_code || '—'}
                </p>

                {/* Reabastecimento nasce da MÁQUINA e espera decisão: o
                    distintivo diz de onde a OS veio antes de o Supervisor
                    decidir aprovar ou negar. */}
                {order.behavior_key === BEHAVIOR_REPLENISHMENT ? (
                    <Badge variant="warning">{label('wms.behavior.REPLENISHMENT')}</Badge>
                ) : null}

                <dl className="space-y-1 text-xs text-mono-600 dark:text-mono-400">
                    <div className="flex items-center justify-between gap-2">
                        <dt>{label('wms.service_order.field.items')}</dt>
                        <dd className="font-mono tabular-nums">
                            {`${progress.closed}/${progress.total}`}
                            {progress.skipped > 0 ? (
                                <span className="ml-1.5 text-warning-700 dark:text-warning-400">
                                    {`(${progress.skipped} ⚠)`}
                                </span>
                            ) : null}
                        </dd>
                    </div>

                    {order.operator_name ? (
                        <div className="flex items-center justify-between gap-2">
                            <dt>{label('wms.service_order.field.operator')}</dt>
                            <dd className="min-w-0 truncate text-right text-mono-700 dark:text-mono-300">
                                {order.operator_name}
                                {order.operator_code ? (
                                    <Id value={order.operator_code} className="ml-1 text-[11px] text-mono-400" />
                                ) : null}
                            </dd>
                        </div>
                    ) : null}

                    {order.route_algorithm ? (
                        <div className="flex items-center justify-between gap-2">
                            <dt>{label('wms.service_order.field.route_algorithm')}</dt>
                            <dd className="truncate text-right">
                                {label(`wms.route.algorithm.${order.route_algorithm}`)}
                            </dd>
                        </div>
                    ) : null}

                    {/* O tempo: rotulado como SLA quando é execução de verdade;
                        sob "Cancelada" quando o trabalho foi interrompido. */}
                    {timing.accepted ? (
                        <div className="flex items-center justify-between gap-2">
                            <dt className="flex items-center gap-1">
                                <Clock size={12} />
                                {isCanceled
                                    ? label('wms.service_order.status.CANCELED')
                                    : label('wms.service_order.field.sla')}
                            </dt>
                            <dd
                                className={`font-mono tabular-nums ${
                                    isCanceled ? 'text-danger-700 dark:text-danger-400' : ''
                                }`}
                                title={
                                    isCanceled
                                        ? label('wms.service_order.status.CANCELED')
                                        : label('wms.service_order.field.sla_help')
                                }
                            >
                                {FormatDurationSec(isCanceled ? timing.elapsed_seconds : timing.seconds)}
                                {timing.running ? ' …' : ''}
                            </dd>
                        </div>
                    ) : null}
                </dl>

                {openPause ? (
                    <p className="flex items-center gap-1.5 rounded bg-warning-soft px-2 py-1 text-[11px] text-warning-800 dark:bg-warning-900/30 dark:text-warning-300">
                        <Warning size={12} className="shrink-0" />
                        {label(`wms.pause.reason.${openPause.reason}`)}
                    </p>
                ) : null}
            </button>

            {/* Fila de DECISÃO: aprovar prepara (READY), negar cancela (§6.5).
                Aprovar é reversível pelo caminho normal do quadro; negar não —
                por isso só ele tem o segundo passo. */}
            {isBacklog && canManage ? (
                <div className="mt-3 flex gap-2 border-t border-mono-100 pt-3 dark:border-mono-900">
                    {confirmingDeny ? (
                        <>
                            {/* Mesmo par da ficha: o botão destrutivo diz o que
                                ACONTECE (cancela a OS), não o que foi clicado. */}
                            <Button
                                variant="danger"
                                size="sm"
                                className="flex-1"
                                loading={busy}
                                onClick={() => onDeny?.(order)}
                            >
                                {`${label('wms.common.cancel')} ${label('wms.service_order.field.code')}`}
                            </Button>
                            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmingDeny(false)}>
                                {label('wms.location_create.back')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="primary"
                                size="sm"
                                className="flex-1"
                                loading={busy}
                                onClick={() => onApprove?.(order)}
                            >
                                <Check size={14} className="mr-1.5" />
                                {label('wms.kanban.approve')}
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                className="flex-1"
                                disabled={busy}
                                onClick={() => setConfirmingDeny(true)}
                            >
                                <X size={14} className="mr-1.5" />
                                {label('wms.kanban.deny')}
                            </Button>
                        </>
                    )}
                </div>
            ) : null}
        </article>
    );
};

export default ServiceOrderCard;
